import React from 'react';

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

export interface RadioGroupProps {
  options: RadioOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  label?: string;
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ options, value, onChange, className = '', label }, ref) => (
    <div ref={ref} className={className}>
      {label && (
        <span className="text-2xs text-content-muted uppercase tracking-wider block mb-2">
          {label}
        </span>
      )}
      <div className="flex flex-col gap-2" role="radiogroup" aria-label={label}>
        {options.map((option) => {
          const isSelected = value === option.value;
          const cardClasses = [
            'p-3 rounded-xl border cursor-pointer transition-all flex items-start gap-3',
            isSelected
              ? 'border-brand/40 bg-brand/5'
              : 'border-subtle bg-surface-primary hover:border-default',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <label key={option.value} className={cardClasses}>
              <input
                type="radio"
                name={label ? `radiogroup-${label}` : 'radiogroup'}
                value={option.value}
                checked={isSelected}
                onChange={() => onChange(option.value)}
                className="sr-only"
                aria-hidden
              />
              {option.icon && (
                <div className="shrink-0 mt-0.5 text-content-secondary">
                  {option.icon}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-content">{option.label}</div>
                {option.description && (
                  <div className="text-xs text-content-tertiary mt-0.5">
                    {option.description}
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  ),
);

RadioGroup.displayName = 'RadioGroup';

export default RadioGroup;
